import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { QRCodeCanvas } from 'qrcode.react';
import { useGeneratePreviewToken } from '../hooks/useGeneratePreviewToken';
import { PreviewTokenResponse } from '@aphp-ema/common/study';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LinkIcon from '@mui/icons-material/Link';
import { useNotification } from '@refinedev/core';

interface StudyPreviewModalProps {
  open: boolean;
  onClose: () => void;
  studyId: string;
  studyName: string;
}

export const StudyPreviewModal: React.FC<StudyPreviewModalProps> = ({
  open,
  onClose,
  studyId,
  studyName,
}) => {
  const { t } = useTranslation();
  const [previewData, setPreviewData] = useState<PreviewTokenResponse | null>(
    null,
  );

  const { open: openNotification } = useNotification();
  const { mutate: generateToken, isLoading } = useGeneratePreviewToken();

  const handleGenerateToken = useCallback(() => {
    generateToken(studyId, {
      onSuccess: (data) => {
        setPreviewData(data);
      },
      onError: () => {
        openNotification?.({
          type: 'error',
          message: t('pages.studies.preview.errorGeneratingToken'),
        });
      },
    });
  }, [studyId, generateToken, openNotification, t]);

  const handleRegenerate = () => {
    setPreviewData(null);
    handleGenerateToken();
  };

  const formatExpirationDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  useEffect(() => {
    if (open && !previewData) {
      handleGenerateToken();
    }
  }, [open, previewData, handleGenerateToken]);

  const handleClose = () => {
    setPreviewData(null);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        aria-labelledby="study-preview-dialog-title"
      >
        <DialogTitle id="study-preview-dialog-title">
          {t('pages.studies.preview.title')} - {studyName}
        </DialogTitle>
        <DialogContent>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!isLoading && !previewData && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('pages.studies.preview.errorGeneratingToken')}
            </Alert>
          )}

          {previewData && (
            <Box sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  ></Box>
                  <Box
                    sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}
                  >
                    <QRCodeCanvas
                      value={previewData.deepLink}
                      size={200}
                      level="M"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('pages.studies.preview.instructions')}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LinkIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">
                      {t('pages.studies.preview.deepLinkLabel')}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={previewData.deepLink}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    sx={{ mb: 2 }}
                  />

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">
                      {t('pages.studies.preview.expiresAt')}
                    </Typography>
                  </Box>
                  <Chip
                    label={formatExpirationDate(previewData.expiresAt)}
                    color="info"
                    icon={<AccessTimeIcon />}
                    sx={{ mb: 2 }}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined" color="secondary">
            {t('pages.studies.preview.closeButton')}
          </Button>
          {previewData && (
            <Button
              onClick={handleRegenerate}
              variant="contained"
              color="primary"
              disabled={isLoading}
            >
              {t('pages.studies.preview.regenerateButton')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
